import {
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { Box, IconButton, Tooltip } from "@mui/material";
import type { GridColDef, GridRenderCellParams, GridRowParams } from "@mui/x-data-grid";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import { AutoDiscoveriesModal } from "../components/auto-discoveries-dialog.tsx";
import { CreatedByColumn } from "../components/created-by-column.tsx";
import { DataGrid } from "../components/data-grid";
import { DiscoveryRunStateChip } from "../components/discovery-run-state-chip.tsx";
import { DiscoveryTriggerModal } from "../components/discovery-trigger-dialog.tsx";
import { PageHeader } from "../components/page-header";
import { useApiQuery } from "../hooks/use-api-query.ts";
import { useQueryParams } from "../hooks/use-query-params.ts";
import type { DiscoveryRun } from "../types.ts";
import { formatDuration } from "../utils";
import { markdownToPlainText } from "../utils/text.ts";

const scenariosColumns: GridColDef<DiscoveryRun>[] = [
  {
    field: "intent",
    headerName: "Description",
    type: "string",
    minWidth: 100,
    flex: 1,
    valueGetter: (value: DiscoveryRun["intent"]) => {
      return markdownToPlainText(value || "No intent");
    },
  },
  {
    field: "state",
    type: "singleSelect",
    valueOptions: ["pending", "waiting", "running", "cancelled", "done", "closed"],
    headerName: "Status",
    renderCell: (params: GridRenderCellParams<DiscoveryRun>) => {
      return <DiscoveryRunStateChip state={params.value} />;
    },
    width: 120,
  },
  {
    field: "created_by",
    type: "string",
    headerName: "Started By",
    width: 150,
    renderCell: CreatedByColumn,
    sortable: false,
  },
  {
    field: "created_at",
    type: "dateTime",
    headerName: "Started At",
    valueGetter: (value: DiscoveryRun["created_at"]) => {
      return new Date(value);
    },
    width: 170,
  },
  {
    field: "tests",
    headerName: "Tests",
    type: "string",
    width: 70,
    valueGetter: (_, row: DiscoveryRun) => {
      const discovered = row.discovered_flows_count || 0;
      const expected = row.flow_count || 0;
      return `${discovered}/${expected}`;
    },
    sortable: false,
  },
  {
    field: "found_issues",
    headerName: "Issues",
    type: "number",
    width: 70,
    valueGetter: (_, row: DiscoveryRun) => {
      return row.found_issues?.length ?? 0;
    },
  },
  {
    field: "elapsed_seconds",
    type: "string",
    headerName: "Duration",
    width: 100,
    valueGetter: (_, row: DiscoveryRun) => formatDuration(row.elapsed_seconds),
  },
];

export default function DiscoveryRunsPage() {
  const { getParam, setParam } = useQueryParams();
  const modalOpen = getParam("triggerOpen") === "true";
  const setModalOpen = (open: boolean) => {
    setParam("triggerOpen", open ? "true" : undefined);
  };
  const autoDiscoveriesModalOpen = getParam("autoDiscoveryOpen") === "true";
  const setAutoDiscoveriesModalOpen = (open: boolean) => {
    setParam("autoDiscoveryOpen", open ? "true" : undefined);
  };
  const nav = useNavigate();

  const {
    data: runs,
    isLoading: isLoadingScnearios,
    refetch: refetchRuns,
  } = useApiQuery<DiscoveryRun[]>("/discovery-runs");

  const someRunning = useMemo(() => {
    return runs?.some((run) => run.state === "running");
  }, [runs]);

  const handleRowClick = (params: GridRowParams<DiscoveryRun>) => {
    nav(`/discovery-runs/${params.row.id}`);
  };

  return (
    <Box data-testid="scenarios-page">
      <PageHeader
        title="Discovery Runs"
        subtitle="See all the discovery runs we built for you to test your application."
        actions={
          <>
            <Tooltip title="Auto-Discoveries" placement="top">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setAutoDiscoveriesModalOpen(true)}
                  sx={{
                    borderRadius: 2,
                    p: 1.5,
                    border: "0px solid",
                    borderColor: "grey.300",
                    color: "grey.800",
                    backgroundColor: "transparent",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "common.white",
                    },
                  }}
                >
                  <AutoAwesomeIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                someRunning
                  ? "A discovery run is already in progress. Please wait for it to finish."
                  : "Discover new user journeys and create tests automatically."
              }
              placement="top"
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => setModalOpen(true)}
                  disabled={someRunning}
                  sx={{
                    borderRadius: 2,
                    p: 1.5,
                    border: "0px solid",
                    borderColor: "grey.300",
                    color: "grey.800",
                    backgroundColor: "transparent",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "common.white",
                    },
                    "&:disabled": {
                      borderColor: "grey.300",
                      backgroundColor: "grey.100",
                      color: "grey.500",
                    },
                  }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Refresh" placement="top">
              <span>
                <IconButton
                  size="small"
                  onClick={() => refetchRuns()}
                  disabled={isLoadingScnearios}
                  sx={{
                    borderRadius: 2,
                    p: 1.5,
                    border: "0px solid",
                    borderColor: "grey.300",
                    color: "grey.800",
                    backgroundColor: "transparent",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "common.white",
                    },
                    "&:disabled": {
                      borderColor: "grey.300",
                      backgroundColor: "grey.100",
                      color: "grey.500",
                    },
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      />
      <DiscoveryTriggerModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <AutoDiscoveriesModal
        open={autoDiscoveriesModalOpen}
        onClose={() => setAutoDiscoveriesModalOpen(false)}
      />
      <DataGrid
        loading={isLoadingScnearios}
        rows={runs ?? []}
        columns={scenariosColumns}
        minHeight={`calc(100vh - 200px)`}
        pageSizeOptions={[25]}
        onRowClick={handleRowClick}
        containerSx={{
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
        }}
      />
    </Box>
  );
}
